// Messaggi dei clienti dall'area clienti (richieste / chat): lo studio legge, risponde e cambia lo stato.
// Quando lo studio risponde, il cliente riceve un'email di avviso (senza il testo) e vede la risposta nell'app.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Send, MessageSquare, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/area-clienti-studio`;
const COLORE_STATO = {
  aperto: 'bg-red-100 text-red-800', in_lavorazione: 'bg-amber-100 text-amber-800', chiuso: 'bg-slate-100 text-slate-600',
};

export default function MessaggiClienti() {
  const [elenco, setElenco] = useState([]);
  const [stati, setStati] = useState({});
  const [filtro, setFiltro] = useState('');
  const [aperto, setAperto] = useState(null);
  const [risposta, setRisposta] = useState('');
  const [invio, setInvio] = useState(false);
  const fondo = useRef(null);

  const carica = useCallback(async () => {
    try {
      const r = (await axios.get(`${API}/ticket`, { params: filtro ? { stato: filtro } : {} })).data;
      setElenco(r.ticket); setStati(r.stati);
    } catch (_) { toast.error('Errore nel caricamento dei messaggi'); }
  }, [filtro]);
  useEffect(() => { carica(); }, [carica]);

  const apri = async (id) => {
    try {
      setAperto((await axios.get(`${API}/ticket/${id}`)).data);
      window.dispatchEvent(new Event('jvservice:messaggi-clienti'));
      carica();
      setTimeout(() => fondo.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (_) { toast.error('Richiesta non trovata'); }
  };
  const rispondi = async () => {
    if (!risposta.trim()) return;
    setInvio(true);
    try {
      await axios.post(`${API}/ticket/${aperto.id}/messaggi`, { testo: risposta });
      setRisposta('');
      toast.success('Risposta inviata: il cliente riceve un avviso');
      await apri(aperto.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Risposta non inviata'); } finally { setInvio(false); }
  };
  const cambiaStato = async (stato) => {
    try { await axios.put(`${API}/ticket/${aperto.id}/stato`, { stato }); await apri(aperto.id); } catch (_) { toast.error('Errore'); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Messaggi clienti</h1>
        <p className="text-slate-600 mt-1">Richieste arrivate dall'area clienti (clienti.crm-jv.com).</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {[['', 'Tutte'], ...Object.entries(stati)].map(([k, t]) => (
          <Button key={k || 'tutte'} size="sm" variant={filtro === k ? 'default' : 'outline'} onClick={() => setFiltro(k)}>{t}</Button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
        <div className="lg:col-span-2 space-y-2">
          {elenco.length === 0 && <Card><CardContent className="p-6 text-slate-500 text-center"><MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />Nessuna richiesta.</CardContent></Card>}
          {elenco.map((t) => (
            <button key={t.id} onClick={() => apri(t.id)}
              className={`w-full text-left rounded-xl border p-3 bg-white hover:border-slate-400 transition ${aperto?.id === t.id ? 'border-slate-900 ring-1 ring-slate-900' : 'border-slate-200'}`}>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900 flex-1 truncate">{t.oggetto}</span>
                {t.non_letti > 0 && <span className="bg-red-600 text-white text-xs rounded-full px-2">{t.non_letti}</span>}
              </div>
              <div className="text-sm text-slate-600 truncate">{t.persona} · {t.servizio}</div>
              {t.ospite && <div className="text-xs text-blue-700 truncate">Non in anagrafica · {t.contatto}</div>}
              <div className="text-xs text-slate-500 truncate mt-1">{t.ultimo}</div>
              <div className="flex items-center gap-2 mt-2">
                <Badge className={COLORE_STATO[t.stato]}>{t.stato_testo}</Badge>
                <span className="text-xs text-slate-400">{t.aggiornato}</span>
              </div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-3">
          {!aperto ? (
            <Card><CardContent className="p-10 text-center text-slate-500">Scegli una richiesta a sinistra.</CardContent></Card>
          ) : (
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-2 border-b pb-3">
                  <div>
                    <div className="text-lg font-semibold text-slate-900">{aperto.oggetto}</div>
                    <div className="text-sm text-slate-600">{aperto.persona} · {aperto.servizio} · aperta il {aperto.creato}</div>
                    {aperto.ospite && <div className="text-sm text-blue-700">Nuovo contatto, non in anagrafica: {aperto.contatto}</div>}
                  </div>
                  <div className="flex gap-1">
                    {Object.entries(stati).map(([k, t]) => (
                      <Button key={k} size="sm" variant={aperto.stato === k ? 'default' : 'outline'} onClick={() => cambiaStato(k)}>{t}</Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-3 py-4 max-h-[55vh] overflow-y-auto">
                  {aperto.messaggi.map((m, i) => (
                    <div key={i} className={`flex ${m.da === 'studio' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.da === 'studio' ? 'bg-green-700 text-white rounded-br-sm' : 'bg-slate-100 text-slate-900 rounded-bl-sm'}`}>
                        <div className="whitespace-pre-wrap text-[15px]">{m.testo}</div>
                        <div className={`text-[11px] mt-1 ${m.da === 'studio' ? 'text-green-100' : 'text-slate-500'}`}>{m.autore} · {m.quando}</div>
                      </div>
                    </div>
                  ))}
                  <div ref={fondo} />
                </div>
                <div className="border-t pt-3 flex gap-2 items-end">
                  <Textarea rows={3} value={risposta} maxLength={3000} placeholder="Scrivi la risposta al cliente…"
                    onChange={(e) => setRisposta(e.target.value)} className="flex-1" />
                  <Button onClick={rispondi} disabled={invio || !risposta.trim()}>
                    {invio ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
